import { string, ref, object, number, boolean, date, array } from 'yup';
import * as yup from 'yup';
import errorMessages from './errorMessages';

const urlValidationRegex = /^(https?:\/\/)([\w.-]+)(:[0-9]+)?(\/[\w.-]*)*(\?[\w=&%.-]*)?(#[\w.-]*)?$/;
const domainRegex = /^(?:(?:https?:\/\/)?(?:www\.)?)?(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?:\/\S*)?$/;

export const createPasswordSchema =
  string()
    .min(8, errorMessages.minLength(8))
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
      errorMessages.passwordStrength
    )
    .required(errorMessages.required);

export const confirmPasswordSchema = (passwordFieldName = 'password') =>
  string()
    .oneOf([ref(passwordFieldName), null], errorMessages.passwordMatch)
    .required(errorMessages.required);

export const dateSchema = date().required(errorMessages.required).typeError(errorMessages.date);

export const emailSchema = string().email(errorMessages.email).required(errorMessages.required);
export const emailSchemaV2 = string().email(errorMessages.email).required(errorMessages.required);


export const otpShema = string().min(6, errorMessages.minLength(6)).required(errorMessages.required);

export const stringRequiredSchema = string().required(errorMessages.required);

export const stringOptionalSchema = string().optional().nullable();

export const booleanRequiredSchema = boolean().required(errorMessages.required);

export const numberRequiredSchema = number().required(errorMessages.required);

export const numberOptionalSchema = number().optional().nullable();

export const select2RequiredSchema = object().shape({
  value: stringRequiredSchema,
  label: stringRequiredSchema,
}).nullable().required(errorMessages.required);

export const select2OptionalSchema = object().shape({
  value: stringOptionalSchema,
  label: stringOptionalSchema,
}).nullable().notRequired();

export const statusSchema = string().oneOf(["active", "inactive", "archived", "discharged"]).required(errorMessages.required);

export const phoneRequiredSchema =
  string()
    .required(errorMessages.required)
    .matches(/^\+?\s*\d+(\s*\d+)*$/, errorMessages.phone)
    .min(8, errorMessages.minDigits(7));

export const phoneRequiredSchemaV2 =
  string()
    .matches(/^\+?\s*\d+(\s*\d+)*$/, errorMessages.phone)
    .min(8, errorMessages.minDigits(7));

export const phoneOptionalSchema =
  string()
    .optional(errorMessages.required)
    .matches(/^\+?\s*\d+(\s*\d+)*$/, errorMessages.phone)
    .min(8, errorMessages.minDigits(7));

export const dateRequiredSchema = date().required(errorMessages.required);

export const dateOptionalSchema = date().optional().nullable();

export const endnDateOptionalSchema =
  date()
    .nullable()
    .optional()
    .min(yup.ref("start_date"), 'Must be greater than start date');

export const endnDateSchema =
  date()
    .required(errorMessages.required)
    .min(yup.ref("start_date"), 'Must be greater than start date');

export const urlRequiredSchema =
  string()
    .required(errorMessages.required)
    .matches(urlValidationRegex, errorMessages.url)

export const urlOptionalSchema =
  string()
    .optional()
    .matches(urlValidationRegex, errorMessages.url)

export const genericFormErrorMsg = "There are errors in the form. Please review your entries and try again.";

export const reportQuestionsValidationSchema = (reportQuestions) => {
  let validationSchema = {};

  reportQuestions?.forEach((question) => {
    let schema = {
      answer: string().nullable()
    };

    if (
      question.is_required === 'yes' &&
      question.question_type !== "date" &&
      question.question_type !== "tracker"
    ) {
      schema.answer = schema.answer.required(errorMessages.required);
    }

    validationSchema[question.id] = object().shape(schema);
  });

  return object().shape(validationSchema);
};

export const optionalPasswordSchema =
  string()
    .min(8, errorMessages.minLength(8))
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
      errorMessages.passwordStrength
    )
    .optional();

export const urlSchema = (options = { required: false }) => {
  let schema = yup.string().trim().matches(domainRegex, errorMessages.url);

  if (options.required) {
    schema = schema.required(errorMessages.required);
  } else {
    schema = schema.notRequired().nullable();
  }

  return schema;
};

export const stringValidation = (name, options = {}) => {
  let schema = string().required(`${name} is required`);

  if (options?.min) {
    schema = schema.min(options?.min, `${name} must be at least ${options?.min} characters`);
  }

  if (options?.max) {
    schema = schema.max(options?.max, `${name} must be at most ${options?.max} characters`);
  }

  return schema;
};